import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Reveal, EASE } from "./Reveal";
import { useLightbox } from "./Lightbox";

export const SLIDES = [
  { src: "/img/hero.jpg", alt: "Round brilliant IGI certified lab grown diamond on black velvet", eyebrow: "Solitaires", caption: "Round brilliant · 1.02 ct · E · VVS1" },
  { src: "/img/ring.jpg", alt: "Emerald cut lab grown diamond engagement ring in 18k yellow gold", eyebrow: "Bespoke rings", caption: "Emerald cut · 1.50 ct · 18k gold" },
  { src: "/img/model.jpg", alt: "Model wearing lab grown diamond drop earrings and a diamond necklace", eyebrow: "Everyday luxury", caption: "Pear drop earrings · 2 × 0.50 ct" },
  { src: "/img/parcel.jpg", alt: "Wholesale calibrated lab grown melee diamond parcel with tweezers", eyebrow: "For jewellers", caption: "Calibrated melee · +4 to -6.5 sieve" },
  { src: "/img/loupe.jpg", alt: "Grading a lab grown diamond under a 10x jeweller's loupe", eyebrow: "Graded by hand", caption: "10× loupe inspection · every stone" },
  { src: "/img/shapes.jpg", alt: "Lab grown diamond shapes chart with ten cuts", eyebrow: "Ten shapes", caption: "Round to Heart · pick your cut" },
];

const AUTOPLAY_MS = 4500;
const variants = {
  enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.4 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0.4 }),
};

export const Carousel = ({ slides = SLIDES }) => {
  const [[index, dir], setState] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const openLightbox = useLightbox();
  const timer = useRef(null);
  const n = slides.length;

  const go = useCallback((next, d) => setState([((next % n) + n) % n, d]), [n]);
  const prev = useCallback(() => go(index - 1, -1), [go, index]);
  const nextSlide = useCallback(() => go(index + 1, 1), [go, index]);

  useEffect(() => { slides.forEach((s) => { const i = new Image(); i.src = s.src; }); }, [slides]);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(nextSlide, AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused, nextSlide]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    if (e.key === "ArrowRight") { e.preventDefault(); nextSlide(); }
  };

  const slide = slides[index];

  return (
    <section id="collection-slider" data-testid="carousel-section" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-4">The collection</p>
            <h2 className="font-display font-light text-ivory text-[clamp(2rem,4.5vw,3.75rem)] leading-[0.95]">Stones for <span className="text-saffron italic">you</span> and for the <span className="text-saffron italic">trade</span>.</h2>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-ivory/40" data-testid="carousel-counter">{String(index + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            role="region" aria-roledescription="carousel" aria-label="Lab grown diamond collection" tabIndex={0}
            data-testid="carousel" onKeyDown={onKey}
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
            className="group relative w-full aspect-[4/3] sm:aspect-[16/10] lg:aspect-[21/9] overflow-hidden bg-[#0d0d0d] border border-gold/25 outline-none focus-visible:ring-1 focus-visible:ring-saffron img-frame"
          >
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div
                key={index} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ x: { duration: 0.7, ease: EASE }, opacity: { duration: 0.5 } }}
                drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.15}
                onDragEnd={(_, info) => { if (info.offset.x < -60 || info.velocity.x < -400) nextSlide(); else if (info.offset.x > 60 || info.velocity.x > 400) prev(); }}
                className="absolute inset-0 cursor-grab active:cursor-grabbing"
                data-testid={`carousel-slide-${index}`}
                aria-roledescription="slide" aria-label={`${index + 1} of ${n}: ${slide.caption}`}
              >
                <img src={slide.src} alt={slide.alt} draggable={false} className="h-full w-full object-cover select-none" />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-night/90 via-night/10 to-transparent" />
                <div className="absolute left-5 bottom-5 sm:left-8 sm:bottom-8 pointer-events-none">
                  <p className="eyebrow">{slide.eyebrow}</p>
                  <p className="font-display text-xl sm:text-3xl text-ivory mt-2" data-testid="carousel-caption">{slide.caption}</p>
                </div>
              </motion.div>
            </AnimatePresence>

            <button data-testid="carousel-prev" onClick={prev} aria-label="Previous image" className="carousel-arrow left-3 sm:left-5"><ChevronLeft size={22} strokeWidth={1.5} /></button>
            <button data-testid="carousel-next" onClick={nextSlide} aria-label="Next image" className="carousel-arrow right-3 sm:right-5"><ChevronRight size={22} strokeWidth={1.5} /></button>
            <button data-testid="carousel-zoom" onClick={() => openLightbox(slide.src, slide.alt)} aria-label="Zoom image" className="absolute top-3 right-3 sm:top-5 sm:right-5 z-10 h-10 w-10 border border-gold/40 bg-night/60 backdrop-blur text-saffron hover:bg-gold/15 hover:border-saffron transition-colors flex items-center justify-center"><Maximize2 size={16} strokeWidth={1.6} /></button>

            <div className="absolute bottom-0 left-0 h-[2px] bg-gold/20 w-full pointer-events-none">
              {!paused && <motion.div key={index} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }} className="h-full bg-saffron" />}
            </div>
          </div>
        </Reveal>

        <div className="mt-6 flex items-center justify-center gap-3" role="tablist" aria-label="Choose slide" data-testid="carousel-dots">
          {slides.map((s, i) => (
            <button
              key={s.src} role="tab" aria-selected={i === index} aria-label={`Go to slide ${i + 1}: ${s.caption}`}
              data-testid={`carousel-dot-${i}`} onClick={() => go(i, i > index ? 1 : -1)}
              className="group/dot p-2 -m-1"
            >
              <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-500 ${i === index ? "w-8 bg-saffron" : "w-1.5 bg-gold/40 group-hover/dot:bg-gold"}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
